import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchData } from '../Helpers/externapi';

const initialConfigState = [];
const initialHospitalImageState = '';

// Only public values used by hospital screens may be requested by the browser.
const publicConfigKeys = new Set([
  'hospitalImagesURL', 'CardFront', 'CardBack', 'LogoWithoutName',
  'couponAmount', 'ConsultationInvoiceBucketURL',
]);
const pendingRequests = new Map();

export const loadConfigValue = createAsyncThunk(
  'configValues/loadValue',
  async (key, { rejectWithValue }) => {
    try {
      if (!pendingRequests.has(key)) {
        pendingRequests.set(key, fetchData('ConfigValues/search', {
          configkey: key, configValue: '', skip: 0, take: 0,
        }));
      }
      const response = await pendingRequests.get(key);
      if (!Array.isArray(response)) {
        throw new Error('Unable to load configuration value');
      }
      const value = response.find((item) => item.ConfigKey === key);
      // Retain only the requested public value, even if search returns other matches.
      return value ? { ConfigKey: key, ConfigValue: value.ConfigValue } : null;
    } catch (error) {
      return rejectWithValue(error?.message || 'Unable to load configuration value');
    } finally {
      pendingRequests.delete(key);
    }
  },
  {
    condition: (key, { getState }) => publicConfigKeys.has(key)
      && !getState().configValues.some((item) => item.ConfigKey === key),
  },
);

const ConfigValuesSlice = createSlice({
  name: 'ConfigValuesSlice',
  initialState: initialConfigState,
  reducers: {
    clearValue: () => initialConfigState,
  },
  extraReducers: (builder) => {
    builder.addCase(loadConfigValue.fulfilled, (state, action) => {
      if (!action.payload) return;
      const index = state.findIndex((item) => item.ConfigKey === action.payload.ConfigKey);
      if (index === -1) state.push(action.payload);
      else state[index] = action.payload;
    });
  },
});

const HospitalImageSlice = createSlice({
  name: 'HospitalImageSlice',
  initialState: initialHospitalImageState,
  reducers: {
    setValue: (state, action) => action.payload,
    clearValue: () => initialHospitalImageState,
  },
});

// Export actions with clear names to avoid conflicts
export const { clearValue: clearConfigValue } = ConfigValuesSlice.actions;
export const { setValue: setHospitalImage, clearValue: clearHospitalImage } = HospitalImageSlice.actions;

// Export reducers with clear names to avoid multiple default exports
export const ConfigValuesReducer = ConfigValuesSlice.reducer;
export const HospitalImageReducer = HospitalImageSlice.reducer;

